<template>
	<div>
        <div class="flex justify-between items-center border-b border-gray-200 pb-2 mb-3">
            <h1 class="module-header">Expenses</h1>
            <div @click="$modal.show('manage-expenses')">
                <button v-if="leases.filtered.length" class="ml-3 flex items-center btn btn-small btn-indigo">
                    <inline-svg name="edit" classes="w-4 h-4 text-white fill-current"></inline-svg>
                    <span class="ml-2">Manage Expenses</span>
                </button>
            </div>
        </div>
        <div class="mb-10">
        	<table v-if="expenses.length" class="w-full">
        		<thead>
                    <tr>
            			<th class="pl-4">Lease</th>
                        <th>Type</th>
                        <th class="hidden lg:table-cell text-center">Start</th>
            			<th class="hidden lg:table-cell text-center">End</th>
            			<th class="text-right pr-8">Base Amount</th>
            			<th>Frequency</th>
            			<th v-if="! fromReatlas" class="text-right pr-8 lg:pr-0">PSF</th>
            			<th v-if="! fromReatlas" class="hidden lg:table-cell text-right">Monthly</th>
            			<th v-if="! fromReatlas" class="hidden lg:table-cell text-right">Annual</th>
            			<th v-if="! fromReatlas" class="hidden lg:table-cell text-center">Liability?</th>
            			<th v-if="! fromReatlas" class="hidden lg:table-cell"></th>
                    </tr>
        		</thead>
        		<tbody>
        			<tr @click="openExpenseModal(expense)" v-for="(expense, expenseIndex) in expenses" :key="expenseIndex" class="highlight-on-hover cursor-pointer">
        				<td class="pl-4">{{ expense.lease_name }}</td>
                        <td>{{ expense.type?.name }}</td>
                        <td class="hidden lg:table-cell text-center">{{ expense.start_date | date }}</td>
        				<td class="hidden lg:table-cell text-center">{{ expense.end_date | date }}</td>
        				<td :data-precision="expense.frequency == 'PSF' ? 2 : 0" :data-originalvalue="expense.amount" class="is-currency text-right pr-8">{{ formatCurrency(expense.amount, expense.frequency == 'PSF' ? 2 : 0) }}</td>
        				<td>{{ expense.frequency }}</td>
        				<td v-if="! fromReatlas" data-precision="2" :data-originalvalue="expense.amount_psf" class="is-currency text-right pr-8 lg:pr-0">{{ formatCurrency(expense.amount_psf, 2) }}</td>
        				<td v-if="! fromReatlas" data-precision="0" :data-originalvalue="expense.amount_monthly" class="is-currency hidden lg:table-cell text-right">{{ formatCurrency(expense.amount_monthly, 0) }}</td>
        				<td v-if="! fromReatlas" data-precision="0" :data-originalvalue="expense.amount_annually" class="is-currency hidden lg:table-cell text-right">{{ formatCurrency(expense.amount_annually, 0) }}</td>
                        <td v-if="! fromReatlas" class="hidden lg:table-cell">
                            <div class="flex justify-center">
                                <div v-if="expense.is_liability == null">-</div>
                                <div v-else>{{ expense.is_liability ? 'Yes' : 'No' }}</div>
                            </div>
                        </td>
                        <td v-if="! fromReatlas" class="text-center max-w-xs">
                            <v-popover v-if="expense.note" trigger="hover click" offset="10">
                                <inline-svg name="note"></inline-svg>

                                <template slot="popover">
                                    <div class="leading-normal max-w-sm p-6">
                                        {{ expense.note }}
                                    </div>
                                </template>
                            </v-popover>
                        </td>
					</tr>
				</tbody>
			</table>

            <div v-else class="none-found">No Expenses Found</div>
		</div>

        <div v-if="costByYear">
            <div class="flex justify-between items-center mb-5 border-b border-gray-300 pb-2">
                <h2 class="text-lg">Expense Timeline</h2>
                <div class="flex items-center">
                    <select v-model="selectedLeaseId" class="form-select form-select-blue text-sm mr-2">
                        <option v-for="lease in leases.all" :value="lease.id" :key="lease.id">{{ lease.name }}</option>
                    </select>
                    <select v-model="costType" class="form-select form-select-blue text-sm mr-2">
                        <option value="cost_by_calendar_year">By Calendar Year</option>
                        <option value="cost_by_term_year">By Term Year</option>
                    </select>
                    <select v-if="! fromReatlas" v-model="selectedYear" class="form-select form-select-blue text-sm mr-2">
                        <option value=''>Year</option>
                        <option v-for="(data, year) in costByYear" :key="year" :value="year">{{ year }}</option>
                    </select>
                    <select v-if="! fromReatlas && costType == 'cost_by_calendar_year'" v-model="selectedMonth" class="hidden form-select form-select-blue text-sm mr-2">
                        <option value=''>Month</option>
                        <option v-for="(month, monthIndex) in months" :key="monthIndex" :value="month.value">{{ month.label }}</option>
                    </select>
                    <a :href="'/sites/' + siteId + '/export/expenses/timeline'">
                        <inline-svg name="xls"></inline-svg>
                    </a>
                </div>
            </div>
            <div class="flex flex-col">
                <div class="flex flex-col lg:flex-row">
                    <div :class="{ 'lg:w-3/5 mr-6': ! fromReatlas, 'w-full': fromReatlas }">
                        <div>
                            <table v-if="selectedLease" class="w-full">
                                <thead>
                                    <tr>
                                        <th class="text-center" :class="{ 'px-4': costType == 'cost_by_term_year'}">Year</th>
                                        <th v-if="costType == 'cost_by_term_year'" class="text-center">Start</th>
                                        <th v-if="costType == 'cost_by_term_year'" class="text-center">End</th>
                                        <th class="text-right">PSF</th>
                                        <th class="text-right">Total</th>
                                        <th class="text-right">Remaining</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(data, year) in costByYear" :key="year">
                                        <td class="text-center" :class="{ 'px-4': costType == 'cost_by_term_year'}">{{ year }}</td>
                                        <td v-if="costType == 'cost_by_term_year'" class="text-center">{{ data.period_start | date }}</td>
                                        <td v-if="costType == 'cost_by_term_year'" class="text-center">{{ data.period_end | date }}</td>
                                        <td data-precision="2" :data-originalvalue="data.psf" class="is-currency text-right">{{ formatCurrency(data.psf, 2) }}</td>
                                        <td data-precision="0" :data-originalvalue="data.total" class="is-currency text-right">{{ formatCurrency(data.total, 0) }}</td>
                                        <td data-precision="0" :data-originalvalue="data.remaining" class="is-currency text-right">{{ formatCurrency(data.remaining, 0) }}</td>
                                    </tr>
                                </tbody>
                                <tfoot class="border-t border-gray-300">
                                    <tr>
                                        <td class="pl-4 text-base" :colspan="costType == 'cost_by_term_year' ? 4 : 2">Totals:</td>
                                        <td data-precision="0" :data-originalvalue="selectedLease.total_cost" class="is-currency text-right text-base">{{ formatCurrency(selectedLease.total_cost, 0) }}</td>
                                        <td data-precision="0" :data-originalvalue="selectedLease.total_remaining" class="is-currency text-right text-base">{{ formatCurrency(selectedLease.total_remaining, 0) }}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                    <div v-if="! fromReatlas" class="w-full lg:w-2/5">
                        <div v-if="selectedYear != ''">
                            <table class="w-full">
                                <thead>
                                    <tr>
                                        <th class="pl-4">Type</th>
                                        <th class="text-right pr-8">Current Cost</th>
                                        <th>Frequency</th>
                                        <th class="text-right">Annual</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="expense in expensesFiltered" :key="expense.id">
                                        <td class="pl-4">{{ expense.type?.name }}</td>
                                        <td :data-precision="expense.frequency == 'PSF' ? 2 : 0" :data-originalvalue="expense.current_cost" class="is-currency text-right pr-8">{{ expense.current_cost }}</td>
                                        <td>{{ expense.frequency }}</td>
                                        <td data-precision="0" :data-originalvalue="expense.annual_cost" class="is-currency text-right">{{ expense.annual_cost }}</td>
                                        <td class="pl-5 text-center max-w-xs">
                                            <v-popover v-if="expense.note" trigger="hover click" offset="10">
                                                <inline-svg name="note"></inline-svg>

                                                <template slot="popover">
                                                    <div class="leading-normal max-w-sm p-6">
                                                        {{ expense.note }}
                                                    </div>
                                                </template>
                                            </v-popover>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else>
                            <p class="m-6 text-sm text-center">Select a Year</p>
                        </div>
                        <div v-if="expensesFiltered.length == 0 && selectedYear != ''">
                            <p class="m-6 text-sm text-center">No Expenses Found</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <modal name="manage-expenses" width="70%" height="auto" click-to-close draggable=".modal-header">
            <manage-leases selectedSection="expenses"></manage-leases>
        </modal>

        <modal
            name="expense-modal"
            :min-width="1072"
            :max-width="1072"
            height="auto"
            click-to-close
            scrollable
            adaptive
            draggable=".expense-title-bar">
                <expense-modal :expense-data="selectedExpense"></expense-modal>
        </modal>
	</div>
</template>

<script>
    import { mapState } from 'vuex';
    import moment from 'moment';
    
	export default {
	    name: 'ReoptExpenses',

		props: {
			data: {
				type: [Object, Array],
				required: true
			},

            siteId: {
                type: Number,
                required: false
            },

            fromReatlas: {
                type: Boolean,
                default: false
            }
		},

        data() {
            return {
                selectedLeaseId: this.data[0]?.id || null,
                costType: 'cost_by_term_year',
                selectedYear: '',
                selectedMonth: '',
                selectedExpense: null,
                months: [
                    { label: 'Jan', value: '1' },
                    { label: 'Feb', value: '2' },
                    { label: 'Mar', value: '3' },
                    { label: 'Apr', value: '4' },
                    { label: 'May', value: '5' },
                    { label: 'Jun', value: '6' },
                    { label: 'Jul', value: '7' },
                    { label: 'Aug', value: '8' },
                    { label: 'Sep', value: '9' },
                    { label: 'Oct', value: '10' },
                    { label: 'Nov', value: '11' },
                    { label: 'Dec', value: '12' },
                ]
            }
        },

        mounted() {
            this.$eventHub.$on('leaseSaved', () => {
                this.$eventHub.$emit('convert-currency');
            });

            this.$eventHub.$on('reloadSite', async () => {
                await this.$store.dispatch('loadSite', this.siteId);
                
                this.leases.all = this.sites.selectedSite.leases;
                this.leases.filtered = this.sites.selectedSite.leases;
            });

            this.$eventHub.$emit('convert-currency');
        },

        watch: {
            selectedLease() {
                this.$eventHub.$emit('convert-currency');
            },

            selectedMonth() {
                this.$eventHub.$emit('convert-currency');
            },

            selectedYear() {
                this.$eventHub.$emit('convert-currency');
            }
        },

        computed: {
            ...mapState(['leases']),
            ...mapState(['sites']),

            expenses() {
                // Filter out leases with id 'new'
                let leases = _.filter(this.leases.all, lease => lease.id != 'new');

                // For each lease, map expenses and parse JSON safely
                return _.flatMap(leases, lease => {
                    return (lease.expenses || []).map(expense => {
                        // Clone expense to avoid mutating original object
                        expense = { ...expense, lease_name: lease.name };

                        // Parse cost_by_calendar_year if it's a string
                        if (typeof expense.cost_by_calendar_year === 'string' && expense.cost_by_calendar_year.trim() !== '') {
                            try {
                                expense.cost_by_calendar_year = JSON.parse(expense.cost_by_calendar_year);
                            } catch (e) {
                                expense.cost_by_calendar_year = {};
                            }
                        }

                        // Parse cost_by_term_year if it's a string
                        if (typeof expense.cost_by_term_year === 'string' && expense.cost_by_term_year.trim() !== '') {
                            try {
                                expense.cost_by_term_year = JSON.parse(expense.cost_by_term_year);
                            } catch (e) {
                                expense.cost_by_term_year = {};
                            }
                        }

                        return expense;
                    });
                });
            },

            expensesFiltered() {
                if (this.selectedYear == "") {
                    return [];
                }

                return _.filter(this.expenses, (expense) => {
                    if (this.costType == 'cost_by_calendar_year') { 
                        const start = moment(expense.start_date);

                        expense.current_cost = this.calculateCurrentCost(expense);
                        expense.annual_cost = this.calculateCurrentAnnualCost(expense)

                        return expense.expensable_id == this.selectedLeaseId
                                && (moment(this.selectedYear + "-01-01").isBetween(expense.start_date, expense.end_date, 'year', '[]')
                                    && this.selectedMonth == ""
                                        ? true
                                        : moment(this.selectedYear + "-" + this.selectedMonth + "-" + start.format("DD")).isBetween(expense.start_date, expense.end_date, 'month', '[]'));
                   
                    } else if (this.costType == 'cost_by_term_year') { 
                        if (typeof this.costByYear[this.selectedYear] == "undefined") {
                            return;
                        }

                        const period_start = this.costByYear[this.selectedYear].period_start;
                        const period_end = this.costByYear[this.selectedYear].period_end;

                        expense.current_cost = this.calculateCurrentCost(expense);
                        expense.annual_cost = this.calculateCurrentAnnualCost(expense)

                        return expense.expensable_id == this.selectedLeaseId
                            && moment(expense.start_date).isSameOrBefore(period_start, 'year')
                            && (moment(expense.end_date).isSameOrAfter(period_end, 'day')
                                || moment(expense.end_date).isBetween(period_start, period_end, 'day', '[]'));
                    }
                });
            },

            selectedLease() {
                return _.find(this.leases.all, ['id', this.selectedLeaseId]);
            },

            costByYear() {
                return this.selectedLease ? this.selectedLease[this.costType] : null;
            },

            chartData() {
                return _.map(this.costByYear, (data, year) => {
                    return { label: year, value: data.total / 100 };
                });
            }
        },

        methods: {
            openExpenseModal(expense) {
                this.selectedExpense = expense;

                this.$modal.show('expense-modal');
            },

            calculateCurrentCost(expense) {
                let annualCost = this.calculateCurrentAnnualCost(expense);

                switch(expense.frequency) {
                    case 'PSF':
                        return this.selectedLease.size ? annualCost / this.selectedLease.size : 0;

                    case 'Monthly':
                        return annualCost / 12;

                    case 'Quarterly':
                        return annualCost / 4;

                    case 'Annual':
                    case 'One Time':
                        return annualCost;

                    case 'Semi-Annual':
                        return annualCost / 2;
                }
            },

            calculateCurrentAnnualCost(expense) {
                if (this.costType == 'cost_by_term_year') {
                    return expense.cost_by_term_year[this.selectedYear].total;
                } else if (this.costType == 'cost_by_calendar_year') {
                    if (this.selectedYear != "" && this.selectedMonth == "") {
                        return expense.cost_by_calendar_year[this.selectedYear]?.total || 0;
                    } else if (this.selectedYear != "" && this.selectedMonth != "") {
                        return expense.cost_by_calendar_year[this.selectedYear]['by_month'][this.selectedMonth];
                    }

                    return 0;
                }
            }
        }
	}
</script>